/**
 * Activity — who used what, for how long, and what they did.
 *
 * Two routes, one screen:
 *  • `/activity/:project` (Advanced → Activity) — one application: its users,
 *    its website's visitors, and the people building it (incl. their Ask-AI
 *    prompts). Also where tracking is switched and tuned for the project.
 *  • `/enterprise/activity` — an enterprise administrator's whole
 *    organization (scope `org`).
 * The report itself is the shared page-runtime ActivityReport — the same one
 * the Run view's System administration and the downloaded runtime show.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Activity, Loader2 } from 'lucide-react'
import ActivityReport from '@twasta/page-runtime/activity/ActivityReport'
import {
  activityAdapter, getActivitySettings, saveActivitySettings,
  type ActivitySettings, type ActivitySettingsResponse,
} from '@/api/activity'

function errText(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' && detail ? detail : fallback
}

const REALMS: { key: string; title: string; body: string }[] = [
  { key: 'app', title: 'Application users',
    body: 'People using the running application — screens opened, time spent, records opened, saved and deleted, action buttons, searches, Assistant questions.' },
  { key: 'site', title: 'Website visitors',
    body: 'Visitors of the published website — pages, time on page, scroll depth, clicks, form submissions, where they came from (referrer, campaign).' },
  { key: 'builder', title: 'Builders',
    body: 'Twasta users building this application — designers opened, changes saved, deployments, and every Ask-AI prompt.' },
]

function SettingsPanel({ project }: { project: string }) {
  const [res, setRes] = useState<ActivitySettingsResponse | null>(null)
  const [saving, setSaving] = useState(false)
  const [denied, setDenied] = useState(false)

  useEffect(() => {
    getActivitySettings(project).then(setRes).catch((e) => {
      const status = (e as { response?: { status?: number } })?.response?.status
      if (status === 403) setDenied(true)
      else toast.error(errText(e, 'Could not load the tracking settings'))
    })
  }, [project])

  const save = useCallback(async (patch: Partial<ActivitySettings>) => {
    setSaving(true)
    try {
      setRes(await saveActivitySettings(project, patch))
      toast.success('Tracking settings saved')
    } catch (e) {
      toast.error(errText(e, 'Could not save the tracking settings'))
    } finally {
      setSaving(false)
    }
  }, [project])

  if (denied) return null
  if (!res) {
    return <div className="card" style={{ padding: 16, marginBottom: 16 }}><Loader2 size={16} className="spin" /></div>
  }
  const s = res.settings
  const inst = res.instance
  const realmOn = (r: string) => (s.realms || []).includes(r)
  const toggleRealm = (r: string) => void save({
    realms: realmOn(r) ? s.realms.filter((x) => x !== r) : [...(s.realms || []), r],
  })

  return (
    <div className="card" style={{ padding: 18, marginBottom: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>Tracking</div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto', fontSize: 14 }}>
          <input type="checkbox" checked={s.enabled} disabled={saving}
                 onChange={() => void save({ enabled: !s.enabled })} />
          Record activity for this application
        </label>
      </div>
      {inst && !inst.enabled && (
        <div style={{ color: 'var(--color-warning, #d97706)', fontSize: 14, marginBottom: 10 }}>
          Activity tracking is switched off for this whole instance (ACTIVITY_ENABLED=false), so nothing is recorded
          whatever is set here.
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10, opacity: s.enabled ? 1 : 0.55 }}>
        {REALMS.map((r) => (
          <label key={r.key} style={{
            display: 'flex', gap: 8, padding: 10, border: '1px solid var(--color-border)', borderRadius: 8,
            cursor: s.enabled ? 'pointer' : 'default',
          }}>
            <input type="checkbox" checked={realmOn(r.key)} disabled={!s.enabled || saving}
                   onChange={() => toggleRealm(r.key)} />
            <span>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{r.title}</span>
              <span style={{ display: 'block', fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>{r.body}</span>
            </span>
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 14, fontSize: 14, alignItems: 'center' }}>
        <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          IP addresses
          <select className="form-input" style={{ width: 'auto', padding: '4px 8px' }} value={s.ip_mode || ''}
                  disabled={saving} onChange={(e) => void save({ ip_mode: (e.target.value || null) as ActivitySettings['ip_mode'] })}>
            <option value="">Instance default ({inst?.ip_mode || 'full'})</option>
            <option value="full">Store in full</option>
            <option value="truncate">Truncate (last block removed)</option>
            <option value="hash">Store a one-way hash</option>
            <option value="none">Do not store</option>
          </select>
        </label>
        <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}
               title="When the website's cookie banner records that a visitor declined analytics, their visits are still counted but carry no lasting visitor id.">
          <input type="checkbox" checked={s.consent !== 'none'} disabled={saving}
                 onChange={() => void save({ consent: s.consent === 'none' ? 'banner' : 'none' })} />
          Respect the cookie banner
        </label>
        <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={s.capture_clicks} disabled={saving}
                 onChange={() => void save({ capture_clicks: !s.capture_clicks })} />
          Record clicks
        </label>
        <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={s.capture_scroll} disabled={saving}
                 onChange={() => void save({ capture_scroll: !s.capture_scroll })} />
          Record scroll depth
        </label>
      </div>
      <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 10 }}>
        Events are kept for {inst?.retention_days ?? 400} days. A published website picks up a change here on its
        next publish; the application and the builder follow it within a minute.
      </div>
    </div>
  )
}

export default function ActivityPage({ scope = 'project' }: { scope?: 'project' | 'org' }) {
  const { project = '' } = useParams<{ project: string }>()
  const navigate = useNavigate()
  const adapter = useMemo(() => activityAdapter(scope, scope === 'project' ? project : ''), [scope, project])

  return (
    <div style={{ padding: '0 32px 32px', maxWidth: 1280 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => (scope === 'project' ? navigate('/workbench') : navigate(-1))}>
          &larr; Back
        </button>
        <Activity size={20} />
        <h1 style={{ fontSize: 23, fontWeight: 600, margin: 0 }}>
          {scope === 'project' ? `Activity: ${project}` : 'Organization activity'}
        </h1>
      </div>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, margin: '0 0 18px 44px' }}>
        {scope === 'project'
          ? 'Who used this application and its website, which pages and screens they opened, how long they stayed, what they did there — and what the people building it changed and asked Ask-AI.'
          : 'Everyone in your organization: what they built and asked Ask-AI, and how your applications and websites are used.'}
      </p>
      {scope === 'project' && project && <SettingsPanel project={project} />}
      <div className="card" style={{ padding: 18 }}>
        <ActivityReport adapter={adapter} showProject={scope !== 'project'} />
      </div>
    </div>
  )
}
